import React, { useState, useEffect, useMemo, useRef } from "react";
import moment from "moment";
import {
  Menu,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Chip,
  Tooltip,
  Box,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
} from "@mui/material";
import StoreSearch from "../../../Campaign/StoreSearch.js";
import Decrypt from "../../../../../shared/utils/Decrypt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import styles from "../../JobBatch.module.css";
import PdfIcon from "../../../../../shared/assets/icons/PdfIcon.png";
import ModalToUpdateBatches from "../../ModalToUpdateBatches";
import axios from "axios";
import ModalToOpenEditJobs from "../../ModalToOpenEditJobs";
import RejectModal from "../../../../../components/AdminportalFolder/Campaign/RejectModal.jsx";
import useRejectModal from "../../../Campaign/useRejectModal.js";

const Accepted = ({
  id,
  updateJob,
  updateJobInParent,
  setTotalAcceptedBatches,
  setAvailableBatches,
  fetchAllJobsBasedOnCampaignId,
  fetchAllBatchesBasedOnCampaignId,
  setIsNotAccepted, // Add this prop
  rejectJob, // Add this prop
  showSnackbar,
  fetchBatchById,
}) => {
  const [expandedBatch, setExpandedBatch] = React.useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [openModalToUpdateBatch, setOpenModalToUpdateBatch] =
    React.useState(false);
  const [detailsOfBatchSelected, setDetailsOfBatchSelected] = React.useState();
  const [isPaymentAmount, setPaymentAmount] = React.useState();
  const [openModal, setOpenModal] = useState(false);
  const [acceptedBatches, setAcceptedBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [jobToReject, setJobToReject] = useState(null);
  const batchRefs = useRef({});
  const containerRef = useRef(null);

  const {
    isProcessing: isRejectProcessing,
    rejectDialogOpen,
    itemInfo,
    openRejectModal,
    closeRejectModal,
    handleRejectConfirm,
  } = useRejectModal({
    onReject: async (ids, reason) => {

      const jobId = ids[0];
      const job = jobToReject;

      let affectedBatchId = null;
      acceptedBatches?.forEach((batch) => {
        if (batch?.jobs?.some((j) => j?._id === jobId)) {
          affectedBatchId = batch?._id;
        }
      });

      setAcceptedBatches((prevBatches) =>
        prevBatches?.map((batch) => {
          if (batch?._id === affectedBatchId) {
            return {
              ...batch,
              jobs: batch?.jobs?.map((j) => {
                if (j?._id === jobId) {
                  return {
                    ...j,
                    status: 2,
                    rejectionReason: reason,
                  };
                }
                return j;
              }),
            };
          }
          return batch;
        })
      );


      await rejectJob(jobId, reason);

      if (affectedBatchId) {
        const updatedBatch = await fetchBatchById(affectedBatchId);

        if (updatedBatch) {
          setAcceptedBatches((prevBatches) =>
            prevBatches?.map((batch) => {
              if (batch?._id === affectedBatchId) {
                const currentBatch = prevBatches?.find(b => b?._id === affectedBatchId);
                return {
                  ...updatedBatch,
                  dateRange: currentBatch?.dateRange || "No Dates Available",
                  isPublished: currentBatch?.isPublished || updatedBatch.isPublished || false,
                };
              }
              return batch;
            })
          );

          const updatedCount = acceptedBatches?.filter(b =>
            !b.jobs?.every((job) => job?.status === 2)
          ).length;
          setTotalAcceptedBatches(updatedCount);
        }
      }

      // Step 5: Clean up
      setJobToReject(null);
      handleCloseMenu();
    },
    showSnackbar,
  });

  const handleOpenRejectModal = (job) => {
    setJobToReject(job);
    openRejectModal(job, job?.storeName || "Job");
    handleCloseMenu();
  };

  const fetchAcceptedBatchBasedOnCampaignId = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("Token not found in localStorage");
        setAcceptedBatches([]);
        setTotalAcceptedBatches(0);
        return;
      }

      const { data } = await axios.get(
        `${process.env.REACT_APP_URL}/jobs/acceptedBatchesForRecce?campaignId=${id}&serviceType=${0}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (data?.success && Array.isArray(data.data)) {
        // Add dateRange to each batch
        const updatedBatches = data.data.map((batch) => {
          if (batch.jobs && batch.jobs.length > 0) {
            const jobDates = batch.jobs.map(
              (job) => new Date(job.jobStartingDate),
            );
            const completionDates = batch.jobs.map(
              (job) => new Date(job.jobCompletionDate),
            );
            const earliestDate = new Date(
              Math.min(...jobDates.map((date) => date.getTime())),
            );
            const latestDate = new Date(
              Math.max(...completionDates.map((date) => date.getTime())),
            );
            batch.dateRange = `${earliestDate.toLocaleDateString()} - ${latestDate.toLocaleDateString()}`;
          } else {
            batch.dateRange = "No Dates Available";
          }
          return batch;
        });

        setAcceptedBatches(updatedBatches);

        // CORRECTED: Simply use the count of all accepted batches
        setTotalAcceptedBatches(updatedBatches.length);
      } else {
        console.warn("Unexpected API response", data);
        setAcceptedBatches([]);
        setTotalAcceptedBatches(0);
      }
    } catch (error) {
      console.error("Error while fetching jobs", error);
      setAcceptedBatches([]);
      setTotalAcceptedBatches(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAcceptedBatchBasedOnCampaignId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // handlers
  const handleAccordionToggle = (batchTitle) => (event) => {
    event.stopPropagation();
    setExpandedBatch((prev) => (prev === batchTitle ? null : batchTitle));
  };

  const handleMenuClick = (event, job) => {
    setAnchorEl(event.currentTarget);
    setSelectedJob(job);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleEditJob = () => {
    setOpenModal(true);
    handleCloseMenu();
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedJob(null); // Reset selected job when modal closes
  };

  // Add this function to handle job updates
  const updateJobInLocalState = async (job) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${process.env.REACT_APP_URL}/jobs/updateJob/${job._id}`,
        job,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data.data; // Return the updated job data
    } catch (error) {
      console.error("Error updating job:", error);
      return null;
    }
  };

  const handleSaveJob = async (updatedFields) => {
    if (!selectedJob || !selectedJob._id) {
      console.error("Selected job does not have an id");
      return;
    }

    const updatedJob = await updateJobInLocalState(updatedFields);
    if (updatedJob) {
      // Update the parent state if the function exists
      updateJobInParent?.(updatedJob);

      // Update the local state (acceptedBatches)
      const updatedBatches = acceptedBatches.map((batch) => ({
        ...batch,
        jobs: batch.jobs.map((job) =>
          job._id === updatedJob._id ? updatedJob : job,
        ),
      }));
      setAcceptedBatches(updatedBatches);

      handleCloseModal();

      // Refresh the data after successful update
      fetchAcceptedBatchBasedOnCampaignId();

      if (showSnackbar) {
        showSnackbar({
          open: true,
          message: "Job updated successfully",
          severity: "success",
        });
      }
    }
  };
  // Handle batch updates when IP is assigned/unassigned
  const handleBatchUpdate = (updatedBatch) => {
    // If the IP was unassigned, move the batch to "Not Accepted"
    if (!updatedBatch.installationPartnerId) {
      setAcceptedBatches((prev) =>
        prev.filter((b) => b._id !== updatedBatch._id),
      );
      if (typeof setIsNotAccepted === "function") {
        setIsNotAccepted((prev) => [...prev, updatedBatch]);
      }
    } else {
      // Otherwise, update the batch in the accepted list
      setAcceptedBatches((prev) =>
        prev.map((b) => (b._id === updatedBatch._id ? updatedBatch : b)),
      );
    }

    // Update the details if this is the currently selected batch
    if (detailsOfBatchSelected?._id === updatedBatch._id) {
      setDetailsOfBatchSelected(updatedBatch);
    }
  };

  const undoCancellation = async () => {
    // logic to undo cancellation (update job by id)
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${process.env.REACT_APP_URL}/jobs/updateJob/${selectedJob?._id}`,
        { jobCancelled: false },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.data) {
        await fetchAcceptedBatchBasedOnCampaignId();
        handleCloseMenu();
        if (showSnackbar) {
          showSnackbar({
            open: true,
            message: "Cancellation undone successfully",
            severity: "success",
          });
        }
      }
      return response.data.data; // Return the updated job data
    } catch (error) {
      console.error("Error updating job:", error);
      if (showSnackbar) {
        showSnackbar({
          open: true,
          message: "Failed to undo cancellation",
          severity: "error",
        });
      }
      return null;
    } finally {
      setIsProcessing(false);
    }
  };
  const approveCancellation = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");
      const response = await axios.put(
        `${process.env.REACT_APP_URL}/jobs/cancelJobRequestedByIP`,
        { jobId: selectedJob._id, approvedBy: userId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.success) {
        await fetchAcceptedBatchBasedOnCampaignId();
        handleCloseMenu();
        if (showSnackbar) {
          showSnackbar({
            open: true,
            message: "Cancellation approved successfully",
            severity: "success",
          });
        }
      } else {
        console.error("API returned success:false", response.data);
        if (showSnackbar) {
          showSnackbar({
            open: true,
            message: response.data.message || "Failed to approve cancellation",
            severity: "error",
          });
        }
      }
      return response.data.data; // Return the updated job data
    } catch (error) {
      console.error("Error approving cancellation:", error);
      if (showSnackbar) {
        showSnackbar({
          open: true,
          message: error.response?.data?.message || "Failed to approve cancellation",
          severity: "error",
        });
      }
      return null;
    } finally {
      setIsProcessing(false);
    }
  };

  // Updated handleRejectJob using the passed rejectJob function
  // Handle the logic to update batches with jobs
  // useEffect(() => {
  //   if (forbatch && acceptedBatches.length > 0) {
  //     const updatedBatches = acceptedBatches.map((batch) => ({
  //       ...batch,
  //       jobs: [],
  //     }));

  //     forbatch.forEach((job) => {
  //       const batch = updatedBatches.find(
  //         (batch) => batch._id === job.batchOfJobsForInstallationPartnerId
  //       );
  //       if (batch) {
  //         batch.jobs.push(job);
  //       }
  //     });

  //     // Calculate the date range for each batch
  //     updatedBatches.forEach((batch) => {
  //       if (batch.jobs.length > 0) {
  //         const jobDates = batch.jobs.map(
  //           (job) => new Date(job.jobStartingDate)
  //         );
  //         const completionDates = batch.jobs.map(
  //           (job) => new Date(job.jobCompletionDate)
  //         );

  //         const earliestDate = new Date(
  //           Math.min(...jobDates.map((date) => date.getTime()))
  //         );
  //         const latestDate = new Date(
  //           Math.max(...completionDates.map((date) => date.getTime()))
  //         );

  //         batch.dateRange = `${earliestDate.toLocaleDateString()} - ${latestDate.toLocaleDateString()}`;
  //       } else {
  //         batch.dateRange = "No Dates Available";
  //       }
  //     });

  //     setAcceptedBatches((prevBatches) => {
  //       const prevBatchesString = JSON.stringify(prevBatches);
  //       const updatedBatchesString = JSON.stringify(updatedBatches);

  //       if (prevBatchesString !== updatedBatchesString) {
  //         return updatedBatches;
  //       } else {
  //         return prevBatches;
  //       }
  //     });

  //     // Update total accepted batches count
  //     const acceptedBatchesCount = updatedBatches.filter(
  //       (batch) => !batch.jobs.every((job) => job.status === 2)
  //     ).length;
  //     setTotalAcceptedBatches(acceptedBatchesCount);
  //   }
  // }, [forbatch, acceptedBatches]);

  // Handle job rejection with reason

  const searchData = useMemo(() => {
    const result = [];

    acceptedBatches?.forEach((batch) => {
      batch?.jobs?.forEach((job) => {
        if (job?.status == 2) return;
        result.push({
          id: job?._id,
          storeName: job?.storeName,
          batchTitle: batch?.batchTitle,
          targetId: batch?._id,
          subText: `Batch: ${batch?.batchTitle}`,
        });
      });
    });

    return result;
  }, [acceptedBatches]);

  return (
    <Grid ref={containerRef} className={`${styles.scrollableContainer}`}>
      <div
        className="mx-3 py-2 sticky top-0 z-20 bg-white"

      >
        <StoreSearch
          data={searchData}
          searchKey="storeName"
          placeholder="Search Store Name"
          containerRef={containerRef}
          targetRefs={batchRefs}
          onSelect={(item) => {
            setExpandedBatch(item?.batchTitle);
          }}
        />
      </div>
      {loading ? (
        <div className={styles.noJobsStyle}>
          <CircularProgress />
        </div>
      ) : acceptedBatches?.length > 0 &&
        acceptedBatches.some(
          (batch) => !batch.jobs?.every((job) => job.status === 2),
        ) ? (
        acceptedBatches
          .filter((batch) => !batch.jobs?.every((job) => job.status === 2))
          .map((batch, index) => {
            // Calculate the total payment for each batch
            const totalPayment = batch.jobs?.reduce(
              (acc, job) => acc + (job?.paymentAmount || 0),
              0,
            );

            return (
              <Grid
                ref={(el) => (batchRefs.current[batch._id] = el)}
                item
                xs={12}
                md={12}
                key={index}
                my={1}
                mx={2}
              >
                <Accordion expanded={expandedBatch === batch.batchTitle}>
                  <AccordionSummary
                    expandIcon={
                      <IconButton
                        onClick={handleAccordionToggle(batch.batchTitle)}
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    }
                    aria-controls={`panel-${index}-content`}
                    id={`panel-${index}-header`}
                  >
                    <div className="flex items-center justify-between p-4 rounded-lg bg-white w-full mx-auto">
                      {/* Section 1: Batch Title */}
                      <div className="flex">
                        <h1 className={`${styles.batchTitle}`}>
                          {batch.batchTitle}
                        </h1>
                      </div>

                      {/* Section 2: Other Details */}
                      <div className={styles.chipContainer}>
                        <div>
                          <Chip
                            label={
                              batch.dateRange
                                ? `Timeline: ${moment(
                                  batch?.dateRange.split(" - ")[0]?.trim(),
                                ).format("DD MMM YY")} - ${moment(
                                  batch?.dateRange.split(" - ")[1]?.trim(),
                                ).format("DD MMM YY")}`
                                : "No date range available"
                            }
                            className={styles.chip}
                          />
                        </div>
                        <Chip
                          key={index}
                          label={
                            batch?.installationPartnerId
                              ? `${batch?.installationPartnerId.name} - ${Decrypt(
                                batch?.installationPartnerId.phoneNumber,
                              ) || "N/A"
                              }`
                              : "No Contact Info"
                          }
                          className={styles.chip}
                        />
                        <Chip
                          label={
                            batch?.jobs?.every((job) => job.status === 2)
                              ? "Complete"
                              : `Pending: ${batch?.jobs?.filter(
                                (job) => job?.status !== 2,
                              ).length || 0
                              }/${batch?.jobs?.length || 0}`
                          }
                          className={
                            batch.jobs?.every((job) => job?.status === 2)
                              ? styles.amountPaid
                              : styles.pending
                          }
                        />
                        {/* Display total payment */}

                        <div>
                          <Chip
                            label={"Batch Payment: ₹" + totalPayment}
                            className={styles.chip}
                          />
                        </div>

                        {/* Ellipsis Button */}
                        <button
                          className="text-gray-400 hover:text-gray-600 text-xl rounded-full px-4 py-1 hover:bg-gray-100"
                          onClick={() => {
                            setOpenModalToUpdateBatch(true);
                            setDetailsOfBatchSelected(batch);
                            setPaymentAmount(
                              batch?.jobs?.reduce(
                                (acc, job) => acc + (job.paymentAmount || 0),
                                0,
                              ),
                            );
                          }}
                        >
                          ⋮
                        </button>
                      </div>
                    </div>
                  </AccordionSummary>

                  <AccordionDetails className={styles.accordionDetail}>
                    {/* Render Job Details Table */}
                    <Box className={`${styles.scrollableBox}`}>
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow className={styles.tableHeader}>
                              <TableCell className={styles.tableCell}>
                                ADDRESS
                              </TableCell>
                              <TableCell className={styles.tableCell}>
                                STORE MANAGER
                              </TableCell>
                              <TableCell className={styles.tableCell}>
                                PRODUCT TYPE
                              </TableCell>
                              <TableCell className={styles.tableCell}>
                                JOB PAY
                              </TableCell>
                              <TableCell className={styles.tableCell}>
                                JOB STATUS
                              </TableCell>
                              <TableCell className={styles.tableCell}>
                                JOB CANCELLED
                              </TableCell>
                              <TableCell className={styles.tableCell}>
                                ACTION
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {batch?.jobs?.map((row, index) => (
                              <TableRow key={index}>
                                <TableCell className={styles.tableCell}>
                                  <div className={styles.sharedTextStyle}>
                                    <span>{row.storeName}</span>
                                  </div>
                                  <div className={styles.address2}>
                                    <span>{row.city}</span>,{" "}
                                    <span>{row.state}</span>,{" "}
                                    <span>{row.zipCode}</span>
                                  </div>
                                </TableCell>
                                <TableCell className={styles.tableCell}>
                                  <div className={styles.sharedTextStyle}>
                                    {row.storeOwnerName}
                                  </div>
                                  <div className={styles.storeManagerContact}>
                                    {row.storeOwnerPhoneNumber}
                                  </div>
                                </TableCell>
                                <TableCell className={styles.tableCell}>
                                  <div className={styles.sharedTextStyle}>
                                    {row?.productId?.productType || "N/A"}
                                  </div>
                                </TableCell>
                                <TableCell className={styles.tableCell}>
                                  <div className={styles.sharedTextStyle}>
                                    ₹{row.paymentAmount}
                                  </div>
                                </TableCell>
                                <TableCell className={styles.tableCell}>
                                  <Chip
                                    label={
                                      row.status === 0
                                        ? "Pending"
                                        : row.status === 2
                                          ? "Complete"
                                          : row.status === 1
                                            ? "In Progress"
                                            : row.status
                                    }
                                    className={
                                      row.status === 2
                                        ? styles.amountPaid
                                        : row.status === 0
                                          ? styles.pending
                                          : row.status === 1
                                            ? styles.inProgress
                                            : ""
                                    }
                                  />
                                </TableCell>
                                <TableCell className={styles.tableCell}>
                                  <Chip
                                    label={
                                      row.jobCancelled
                                        ? row.status === 2
                                          ? "Rejected"
                                          : "Requested for cancellation"
                                        : "-"
                                    }
                                    className={
                                      row.jobCancelled
                                        ? row.status === 2
                                          ? styles.blacklisted
                                          : styles.pending
                                        : "-"
                                    }
                                  />
                                </TableCell>
                                <TableCell className={styles.tableCell}>
                                  {row.status != 2 ? (
                                    <Tooltip title="Actions">
                                      <IconButton
                                        onClick={(event) => {
                                          handleMenuClick(event, row);
                                        }}
                                        disabled={isProcessing || isRejectProcessing}
                                        sx={{
                                          "&:hover": {
                                            backgroundColor: "transparent",
                                            "& .MuiSvgIcon-root": {
                                              color: (theme) =>
                                                theme.palette.action.disabled,
                                            },
                                          },
                                        }}
                                      >
                                        <MoreVertIcon />
                                      </IconButton>
                                    </Tooltip>
                                  ) : (
                                    <Tooltip title="No actions available">
                                      <span>
                                        <IconButton disabled>
                                          <MoreVertIcon color="disabled" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            );
          })
      ) : (
        <div className={styles.noJobsStyle}>
          <Typography
            variant="h6"
            align="center"
            className={`${styles.noJobsBox} ${styles.noJobsText}`}
          >
            No batches Available
          </Typography>
        </div>
      )}
      {openModalToUpdateBatch && (
        <ModalToUpdateBatches
          fetchUpdatedBatchDetails={fetchAcceptedBatchBasedOnCampaignId}
          openModal={openModalToUpdateBatch}
          setOpenModalToUpdateBatch={setOpenModalToUpdateBatch}
          batch={detailsOfBatchSelected}
          setAvailableBatches={setAvailableBatches}
          fetchAllJobsBasedOnCampaignId={fetchAllJobsBasedOnCampaignId}
          fetchAllBatchesBasedOnCampaignId={fetchAllBatchesBasedOnCampaignId}
          isPaymentAmount={isPaymentAmount}
          onBatchUpdate={handleBatchUpdate} // Pass the handler
          setAcceptedBatches={setAcceptedBatches} // Pass setter
          setIsNotAccepted={setIsNotAccepted} // Pass setter
        />
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleEditJob} disabled={isProcessing || isRejectProcessing}>
          Edit Job
        </MenuItem>
        {selectedJob?.jobCancelled && (
          <MenuItem onClick={undoCancellation} disabled={isProcessing || isRejectProcessing}>
            {isProcessing ? "Processing..." : "Undo Cancellation"}
          </MenuItem>
        )}
        {selectedJob?.jobCancelled && (
          <MenuItem onClick={approveCancellation} disabled={isProcessing || isRejectProcessing}>
            {isProcessing ? "Processing..." : "Approve Cancellation"}
          </MenuItem>
        )}
        {selectedJob?.status != 2 && (
          <MenuItem
            onClick={() => {
              handleOpenRejectModal(selectedJob);
            }}
            disabled={isProcessing || isRejectProcessing}
          >
            {isProcessing || isRejectProcessing ? "Processing..." : "Reject Job"}
          </MenuItem>
        )}
      </Menu>
      <ModalToOpenEditJobs
        open={openModal}
        handleClose={handleCloseModal}
        selectedJob={selectedJob}
        setSelectedJob={setSelectedJob}
        handleSaveJob={handleSaveJob}
        showJobPayField={false}
      />

      <RejectModal
        open={rejectDialogOpen}
        onClose={closeRejectModal}
        onConfirm={handleRejectConfirm}
        title="Reject Job"
        isProcessing={isRejectProcessing}
        confirmButtonText="Reject"
        cancelButtonText="Cancel"
        placeholder="Enter rejection reason..."
        rows={4}
        itemName={itemInfo.name}
        itemCount={itemInfo.count}
      />
    </Grid>
  );
};

export default Accepted;
